/**
 * [S20 2026-09-14] REAKCJE (EMOJI) POD WIADOMOŚCIAMI CZATU TEAMS — logika bez wyglądu.
 *
 * Polecenie właściciela: „w widget teams dodaj mi opcję dodawania emotikon do odpowiedzi i je wyświetlaj,
 * chodzi o dodawanie reakcji do czyjejś wiadomości.”
 *
 * DECYZJE (zapisane w rozliczeniu S20):
 *  • reagujemy na CUDZE wiadomości — tak brzmi polecenie; na własnych reakcje są tylko wyświetlane;
 *  • stały zestaw sześciu szybkich reakcji Teams (lubię to, serce, śmiech, zaskoczenie, smutek, złość)
 *    plus „więcej” z tą samą listą emoji, którą ma pole pisania (EMOJI_CZATU) — bez dowolnego wpisywania;
 *  • klik w pigułkę PRZEŁĄCZA moją reakcję: moja podświetlona → zdejmuję, cudza → dodaję taką samą.
 *
 * Klucz reakcji ignoruje selektor wariantu U+FE0F: „❤️” z palety i „❤” z Graph to ta sama reakcja.
 */
import { KOD_TEAMS } from "./teamsBlad";

/** Sześć szybkich reakcji Teams — w tej kolejności, w jakiej pokazuje je klient Teams. */
export const SZYBKIE_REAKCJE = Object.freeze([
    Object.freeze({ znak: "👍", nazwa: "Lubię to" }),
    Object.freeze({ znak: "❤️", nazwa: "Serce" }),
    Object.freeze({ znak: "😂", nazwa: "Śmiech" }),
    Object.freeze({ znak: "😮", nazwa: "Zaskoczenie" }),
    Object.freeze({ znak: "😢", nazwa: "Smutek" }),
    Object.freeze({ znak: "😡", nazwa: "Złość" }),
]);

/** Lista emoji pola pisania — jedno źródło dla wstawiania do tekstu i dla „więcej reakcji”. */
export const EMOJI_CZATU = Object.freeze([
    "😀", "😁", "😂", "🤣", "😊", "😍", "😎", "😉", "😅", "🙂", "😘", "😢", "😡", "🤔", "😴", "🤝",
    "👍", "👎", "🙏", "👏", "🔥", "💯", "✅", "❌", "❤️", "🎉", "🙌", "👌", "💪", "🚀", "⭐", "👋",
]);

/** Klucz porównawczy reakcji: bez U+FE0F, bez spacji, małymi literami (starsze nazwy „like” / „LIKE”). */
export const kluczReakcji = (znak) => String(znak == null ? "" : znak).replace(new RegExp(String.fromCharCode(0xfe0f), "g"), "").trim().toLowerCase();

const kluczPozycji = (r) => kluczReakcji(r?.emoji || r?.type);

/** Pozycja reakcji odpowiadająca znakowi albo null. */
export const znajdzReakcje = (reakcje, znak) => {
    const klucz = kluczReakcji(znak);
    if (!klucz || !Array.isArray(reakcje)) return null;
    return reakcje.find((r) => kluczPozycji(r) === klucz) || null;
};

/** Znaki moich reakcji pod wiadomością — pasek reakcji podświetla je jako wybrane. */
export const mojeReakcje = (reakcje) =>
    (Array.isArray(reakcje) ? reakcje : []).filter((r) => r?.reactedByMe).map((r) => r.emoji || r.type);

/**
 * Wiadomość z przełączoną MOJĄ reakcją (zmiana optymistyczna, zanim odpowie serwer). Nie mutuje wejścia.
 * @param {object} wiadomosc
 * @param {string} znak
 * @param {boolean} zdejmij true = zdejmuję swoją reakcję, false = dodaję
 */
export const przelaczReakcjeWWiadomosci = (wiadomosc, znak, zdejmij) => {
    const reakcje = Array.isArray(wiadomosc?.reactions) ? wiadomosc.reactions : [];
    const klucz = kluczReakcji(znak);
    if (!klucz) return wiadomosc;
    const idx = reakcje.findIndex((r) => kluczPozycji(r) === klucz);

    if (zdejmij) {
        if (idx < 0 || !reakcje[idx].reactedByMe) return wiadomosc;
        const r = reakcje[idx];
        const count = Math.max(0, (Number(r.count) || 0) - 1);
        const nowe = count === 0
            ? reakcje.filter((_, i) => i !== idx)
            : reakcje.map((x, i) => (i === idx
                ? { ...r, count, reactedByMe: false, myReactionType: null, users: (r.users || []).filter((u) => !u?.isMe) }
                : x));
        return { ...wiadomosc, reactions: nowe };
    }

    if (idx >= 0) {
        const r = reakcje[idx];
        if (r.reactedByMe) return wiadomosc;
        const nowe = reakcje.map((x, i) => (i === idx
            ? { ...r, count: (Number(r.count) || 0) + 1, reactedByMe: true, myReactionType: znak, users: [...(r.users || []), { isMe: true }] }
            : x));
        return { ...wiadomosc, reactions: nowe };
    }

    return {
        ...wiadomosc,
        reactions: [...reakcje, {
            type: znak, emoji: znak, count: 1, reactedByMe: true, myReactionType: znak, sourceTypes: [znak], users: [{ isMe: true }],
        }],
    };
};

const formaOsob = (n) => {
    if (n === 1) return "osoba";
    const jednosci = n % 10;
    const setki = n % 100;
    return jednosci >= 2 && jednosci <= 4 && (setki < 12 || setki > 14) ? "osoby" : "osób";
};

const formaInnych = (n) => {
    if (n === 1) return "inna osoba";
    return formaOsob(n) === "osoby" ? "inne osoby" : "innych osób";
};

/**
 * Kto zareagował, po polsku: „Ty, Anna Nowak”, „Anna Nowak i 2 inne osoby”, „3 osoby”.
 * Graph często nie podaje nazwy osoby w reakcji — szukamy jej wśród uczestników rozmowy po OID.
 * @param {object} reakcja pozycja z `message.reactions`
 * @param {Array<{userId?: string, displayName?: string}>} [czlonkowie] uczestnicy rozmowy
 */
export const opisOsobReakcji = (reakcja, czlonkowie = []) => {
    const liczba = Math.max(0, Number(reakcja?.count) || 0);
    let ja = !!reakcja?.reactedByMe;
    const imiona = [];
    for (const u of Array.isArray(reakcja?.users) ? reakcja.users : []) {
        if (u?.isMe) { ja = true; continue; }
        const oid = String(u?.userId || "").toLowerCase();
        const zRozmowy = oid ? (czlonkowie || []).find((c) => String(c?.userId || "").toLowerCase() === oid) : null;
        const nazwa = String(u?.displayName || zRozmowy?.displayName || "").trim();
        if (nazwa && !imiona.includes(nazwa)) imiona.push(nazwa);
    }
    const nazwane = [...(ja ? ["Ty"] : []), ...imiona];
    const reszta = Math.max(0, liczba - nazwane.length);
    if (nazwane.length === 0) return liczba > 0 ? `${liczba} ${formaOsob(liczba)}` : "";
    if (reszta === 0) return nazwane.join(", ");
    return `${nazwane.join(", ")} i ${reszta} ${formaInnych(reszta)}`;
};

/**
 * Zdanie dla człowieka, gdy reakcji nie udało się zapisać — rozstrzygane PO KODZIE z serwera (utils/teamsBlad).
 * Brak zgody tenanta na ChatMessage.Send też kończy się w Graph odmową 403, więc zdanie o uprawnieniach mówi i o tym.
 */
export const opisBleduReakcji = (kod) => {
    if (kod === KOD_TEAMS.BRAK_UPRAWNIEN) {
        return "Nie możesz reagować w tej rozmowie — konto Microsoft nie ma do tego uprawnień. Ponowne łączenie konta "
            + "tego nie zmieni: dostęp nadaje organizator rozmowy, a zgodę aplikacji na reakcje — administrator Microsoft 365.";
    }
    if (kod === KOD_TEAMS.TOKEN_WYGASL) {
        return "Sesja Teams wygasła — reakcja nie została zapisana. Połącz ponownie konto Microsoft w Integracjach.";
    }
    if (kod === KOD_TEAMS.NIEDOSTEPNE) {
        return "Microsoft Teams chwilowo nie odpowiada — reakcja nie została zapisana. Spróbuj ponownie za chwilę.";
    }
    return "Nie udało się zapisać reakcji. Spróbuj ponownie.";
};

/**
 * Przełącza MOJĄ reakcję: zmiana optymistyczna na liście wiadomości → żądanie → przy błędzie cofnięcie zmiany.
 * Wydzielone z kontekstu czatu, żeby dało się to sprawdzić bez uruchamiania całego widgetu z odpytywaniem.
 *
 * @param {object} p
 * @param {{setTeamsMessageReaction: Function, unsetTeamsMessageReaction: Function}} p.api
 * @param {string} p.chatId
 * @param {object} p.wiadomosc wiadomość w stanie z chwili kliknięcia
 * @param {string} p.znak reakcja (znak emoji)
 * @param {(fn: (prev: object[]) => object[]) => void} p.ustawWiadomosci setter listy wiadomości
 * @returns {Promise<{ok: boolean, zdjeta: boolean, blad?: any}>}
 */
export const przelaczReakcjeZSerwerem = async ({ api, chatId, wiadomosc, znak, ustawWiadomosci }) => {
    if (!chatId || !wiadomosc?.id || !kluczReakcji(znak)) return { ok: false, zdjeta: false };

    const istniejaca = znajdzReakcje(wiadomosc.reactions, znak);
    const zdejmij = !!istniejaca?.reactedByMe;
    const poprzednie = wiadomosc.reactions;

    ustawWiadomosci((prev) => prev.map((m) => (m.id === wiadomosc.id ? przelaczReakcjeWWiadomosci(m, znak, zdejmij) : m)));

    try {
        if (zdejmij) {
            await api.unsetTeamsMessageReaction(chatId, wiadomosc.id, istniejaca.myReactionType || istniejaca.emoji || znak);
        } else {
            await api.setTeamsMessageReaction(chatId, wiadomosc.id, znak);
        }
        return { ok: true, zdjeta: zdejmij };
    } catch (blad) {
        ustawWiadomosci((prev) => prev.map((m) => (m.id === wiadomosc.id ? { ...m, reactions: poprzednie } : m)));
        return { ok: false, zdjeta: zdejmij, blad };
    }
};
